<template>
    <div>
        <modal name="modal-add-invoice" height="auto" :scrollable="true" width="850px">
            <div v-if="selectedChantier" class="p-6">
                <!-- <h2 class="text-xl font-bold text-gray-900 mb-4">Importer une facture</h2> -->
                <invoice-ai-form @invoice-created="$modal.hide('modal-add-invoice'); getAlerts();" :chantier-id="selectedChantier.id" />
            </div>
        </modal>

        <app-template>
            <div class="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
                <div class="mb-8">
                    <h1 class="text-3xl font-extrabold text-gray-900 tracking-tight">Facturation</h1>
                    <p class="mt-2 text-sm text-gray-500">
                        Gérez les chantiers en attente de facturation.
                    </p>
                </div>

                <div v-if="loadingGetAlert" class="bg-white py-12 flex flex-col items-center justify-center text-gray-400 rounded-xl shadow-sm border border-gray-200">
                    <i class="fas fa-circle-notch fa-spin text-4xl text-red-500 mb-4"></i>
                    <p class="text-sm font-medium text-gray-600">Recherche des factures en cours...</p>
                </div>

                <div v-if="!loadingGetAlert" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="bg-gray-50/50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                        <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <i class="fas fa-exclamation-circle text-red-500"></i>
                            Chantiers à facturer
                        </h2>
                        <span v-if="!loadingGetAlert && alerts.length > 0" class="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">
                            {{ alertsAFacturer.length }} chantier(s) en attente
                        </span>
                    </div>

                    <div class="p-6 bg-gray-50">
                        <div v-if="!loadingGetAlert">
                            <div v-if="alertsAFacturer.length === 0" class="py-12 flex flex-col items-center justify-center text-center bg-white rounded-lg border border-dashed border-gray-300">
                                <div class="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-4">
                                    <i class="fas fa-check text-2xl"></i>
                                </div>
                                <h3 class="text-lg font-medium text-gray-900">Tout est facturé !</h3>
                                <p class="mt-1 text-sm text-gray-500">Aucun chantier n'est en attente de facturation.</p>
                            </div>

                            <div v-else class="space-y-4">
                                <div 
                                    v-for="alert in alertsAFacturer" 
                                    :key="alert.id" 
                                    class="bg-white border rounded-lg overflow-hidden transition-all duration-200"
                                    :class="expandedAlerts.includes(alert.id) ? 'border-red-300 shadow-md' : 'border-gray-200 hover:border-red-200 shadow-sm'"
                                >
                                    <div 
                                        @click="toggleAlert(alert.id)"
                                        class="cursor-pointer px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none group"
                                    >
                                        <div class="flex items-start gap-4">
                                            <div class="mt-1 bg-red-50 text-red-500 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <i class="fas fa-file-invoice-dollar text-lg"></i>
                                            </div>
                                            <div>
                                                <h3 class="text-base font-bold text-gray-900 uppercase">
                                                    {{ getClientName(alert) }}
                                                </h3>
                                                <p class="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                                                    <i class="fas fa-map-marker-alt text-gray-400"></i>
                                                    {{ getAddress(alert) }}
                                                </p>
                                                <p class="text-xs text-gray-400 mt-1">Chantier #{{ alert.id }}</p>
                                            </div>
                                        </div>

                                        <div class="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-100">
                                            <div class="bg-red-100 text-red-700 font-bold px-3 py-1.5 rounded-md flex items-center gap-2 text-sm">
                                                <i class="fas fa-exclamation-triangle"></i>
                                                {{ alert.interventions?.length || 0 }} intervention(s) à facturer
                                            </div>
                                            <i 
                                                class="fas fa-chevron-down text-gray-400 transition-transform duration-300 group-hover:text-red-500"
                                                :class="{ 'transform rotate-180': expandedAlerts.includes(alert.id) }"
                                            ></i>
                                        </div>
                                    </div>

                                    <div 
                                        v-show="expandedAlerts.includes(alert.id)"
                                        class="border-t border-gray-100 bg-gray-50"
                                    >
                                        <ul v-if="alert.interventions && alert.interventions.length > 0" class="divide-y divide-gray-200">
                                            <li 
                                                v-for="intervention in alert.interventions" 
                                                :key="intervention.id" 
                                                class="p-4 pl-16 hover:bg-gray-100/50 transition-colors flex flex-col sm:flex-row justify-between gap-4"
                                            >
                                                <div>
                                                    <div class="text-sm font-semibold text-gray-800">
                                                        {{ intervention.intervention_type?.nom }}
                                                    </div>
                                                    <div class="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                                        <span class="font-mono bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">{{ intervention.reference }}</span>
                                                        <span>•</span>
                                                        <span><i class="fas fa-wrench text-gray-400 mr-1"></i> {{ intervention.appareil.marque }} {{ intervention.appareil.model }}</span>
                                                    </div>
                                                </div>
                                                <div class="flex-shrink-0 flex items-center">
                                                    <span 
                                                        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-white shadow-sm"
                                                        :style="{ color: intervention.statut.couleur, borderColor: intervention.statut.couleur }"
                                                    >
                                                        <i :class="intervention.statut.icone" class="mr-1.5"></i> 
                                                        {{ intervention.statut.libelle }}
                                                    </span>
                                                </div>
                                            </li>
                                        </ul>
                                        
                                        <div class="p-4 bg-white border-t border-gray-100 flex justify-end">
                                            <button @click="selectedChantier = alert; $modal.show('modal-add-invoice')" class="bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 px-4 rounded shadow transition-colors">
                                                <i class="fas fa-upload mr-1"></i> Importer la facture
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!loadingGetAlert" class="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div class="bg-gray-50/50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                        <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <i class="fas fa-exclamation-circle text-red-500"></i>
                            Retards de paiement
                        </h2>
                        <span v-if="!loadingGetAlert && alerts.length > 0" class="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">
                            {{ alertsRetard.length }} facture(s) en retard
                        </span>
                    </div>

                    <div class="p-6 bg-gray-50">
                        <div v-if="!loadingGetAlert">
                            <div v-if="alertsRetard.length === 0" class="py-12 flex flex-col items-center justify-center text-center bg-white rounded-lg border border-dashed border-gray-300">
                                <div class="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-4">
                                    <i class="fas fa-check text-2xl"></i>
                                </div>
                                <h3 class="text-lg font-medium text-gray-900">Aucun retard !</h3>
                                <p class="mt-1 text-sm text-gray-500">Aucune facture n'a de retard de paiement.</p>
                            </div>

                            <div v-else class="space-y-4">
                                <div 
                                    v-for="alert in alertsRetard" 
                                    :key="alert.id" 
                                    class="bg-white border rounded-lg overflow-hidden transition-all duration-200"
                                    :class="expandedAlerts.includes(alert.id) ? 'border-red-300 shadow-md' : 'border-gray-200 hover:border-red-200 shadow-sm'"
                                >
                                    <div 
                                        @click="toggleAlert(alert.id)"
                                        class="cursor-pointer px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none group"
                                    >
                                        <div class="flex items-start gap-4">
                                            <div class="mt-1 bg-red-50 text-red-500 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <i class="fas fa-file-invoice-dollar text-lg"></i>
                                            </div>
                                            <div>
                                                <h3 class="text-base font-bold text-gray-900 uppercase">
                                                    {{ getClientName(alert) }}
                                                </h3>
                                                <p class="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                                                    <i class="fas fa-map-marker-alt text-gray-400"></i>
                                                    {{ getAddress(alert) }}
                                                </p>
                                                <p class="text-xs text-gray-400 mt-1">Chantier #{{ alert.id }}</p>
                                            </div>
                                        </div>

                                        <div class="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-100">
                                            <div class="bg-red-100 text-red-700 font-bold px-3 py-1.5 rounded-md flex items-center gap-2 text-sm">
                                                <i class="fas fa-exclamation-triangle"></i>
                                                {{ alert.interventions?.length || 0 }} facture(s) en retard
                                            </div>
                                            <i 
                                                class="fas fa-chevron-down text-gray-400 transition-transform duration-300 group-hover:text-red-500"
                                                :class="{ 'transform rotate-180': expandedAlerts.includes(alert.id) }"
                                            ></i>
                                        </div>
                                    </div>

                                    <div 
                                        v-show="expandedAlerts.includes(alert.id)"
                                        class="border-t border-gray-100 bg-gray-50"
                                    >
                                        <ul v-if="alert.interventions && alert.interventions.length > 0" class="divide-y divide-gray-200">
                                            <li 
                                                v-for="intervention in alert.interventions" 
                                                :key="intervention.id" 
                                                class="p-4 pl-16 hover:bg-gray-100/50 transition-colors flex flex-col sm:flex-row justify-between gap-4"
                                            >
                                                <div>
                                                    <div class="text-sm font-semibold text-gray-800">
                                                        {{ intervention.intervention_type?.nom }}
                                                    </div>
                                                    <div class="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                                        <span class="font-mono bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">{{ intervention.reference }}</span>
                                                        <span>•</span>
                                                        <span><i class="fas fa-wrench text-gray-400 mr-1"></i> {{ intervention.appareil.marque }} {{ intervention.appareil.model }}</span>
                                                    </div>
                                                </div>
                                                <div class="flex-shrink-0 flex items-center">
                                                    <span 
                                                        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-white shadow-sm"
                                                        :style="{ color: intervention.statut.couleur, borderColor: intervention.statut.couleur }"
                                                    >
                                                        <i :class="intervention.statut.icone" class="mr-1.5"></i> 
                                                        {{ intervention.statut.libelle }}
                                                    </span>
                                                </div>
                                            </li>
                                        </ul>
                                        
                                        <div class="p-4 bg-white border-t border-gray-100 flex justify-end">
                                            <button @click="selectedChantier = alert; $modal.show('modal-add-invoice')" class="bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 px-4 rounded shadow transition-colors">
                                                <i class="fas fa-upload mr-1"></i> Importer la facture
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </app-template>
    </div>
</template>

<script>
import InvoiceAiForm from './Components/InvoiceAiForm.vue';

export default {
    components: {
        InvoiceAiForm
    },

    data() {
        return {
            alerts: [],
            loadingGetAlert: false,
            expandedAlerts: [], // Stocke les IDs des alertes déroulées

            selectedChantier: null, // Chantier sélectionné pour l'import de facture
        }
    },

    computed: {
        alertsAFacturer() {
            return this.alerts.filter(alert => alert.invoices.length == 0);
        },
        alertsRetard() {
            return this.alerts.filter(alert => alert.invoices.length > 0)
        }
    },

    methods: {
        async getAlerts() {
            try {
                this.loadingGetAlert = true;
                const response = await this.$request.post('/factures/get-by-chantiers');
                this.alerts = response;
                
                // Optionnel : Ouvrir automatiquement le premier chantier s'il y en a
                if (this.alerts.length > 0) {
                    this.expandedAlerts.push(this.alerts[0].id);
                }
            } catch (error) {
                console.error('Error fetching alerts:', error);
            } finally {
                this.loadingGetAlert = false;
            }
        },

        toggleAlert(id) {
            const index = this.expandedAlerts.indexOf(id);
            if (index === -1) {
                this.expandedAlerts.push(id);
            } else {
                this.expandedAlerts.splice(index, 1);
            }
        },

        // Récupère le nom du client depuis la première intervention
        getClientName(alert) {
            if (!alert.interventions || alert.interventions.length === 0) return 'Client inconnu';
            const client = alert.interventions[0].client;
            if (!client) return 'Client inconnu';
            
            // Gestion pro vs particulier si besoin, ici on utilise nom/prénom
            return `${client.nom} ${client.prenom || ''}`.trim();
        },

        // Récupère l'adresse formatée depuis la première intervention
        getAddress(alert) {
            if (!alert.interventions || alert.interventions.length === 0) return 'Adresse inconnue';
            const adresse = alert.interventions[0].adresse;
            if (!adresse) return 'Adresse inconnue';
            
            const parts = [adresse.adresse1, adresse.code_postal, adresse.ville].filter(Boolean);
            return parts.join(', ');
        }
    },

    mounted() {
        this.getAlerts()
    }
}
</script>

<style scoped>
/* Les animations de transition Tailwind gèrent la fluidité de l'interface */
</style>